import {memo, ReactNode} from "react";
import {Box, Popover, Table} from "@chakra-ui/react";
import {Portal} from "../../../../components/Portal";
import {ControlNode} from "./useLayeredGraph";
import RelianceBadge from "./RelianceBadge";
import {ConclusionValue} from "./conclusionValue";

type Props = {
  node: ControlNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The element that anchors the popover (the card itself). */
  children: ReactNode;
};

/**
 * Per-scope conclusions for a control carrying more than one. The card holds the control identity,
 * so the popover is just the table.
 */
const ScopedControlPopover = memo(({node, open, onOpenChange, children}: Props) => (
  <Popover.Root
    open={open}
    onOpenChange={e => onOpenChange(e.open)}
    positioning={{placement: "right-start", gutter: 12}}
    lazyMount
    unmountOnExit
  >
    <Popover.Trigger asChild>{children}</Popover.Trigger>
    <Portal>
      <Popover.Positioner>
        <Popover.Content width="auto" minW="360px" maxW="520px" p={0} onClick={e => e.stopPropagation()}>
          <Popover.Arrow>
            <Popover.ArrowTip />
          </Popover.Arrow>
          <Popover.Body p={0}>
            <Box p={3}>
              <Table.Root size="sm" variant="line" css={{"& tbody tr:last-of-type td": {borderBottom: "none"}}}>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader px={2} py={1} fontSize="xs" color="fg.muted" fontWeight="normal">
                      Scope
                    </Table.ColumnHeader>
                    <Table.ColumnHeader px={2} py={1} fontSize="xs" color="fg.muted" fontWeight="normal">
                      Operating effectiveness
                    </Table.ColumnHeader>
                    <Table.ColumnHeader px={2} py={1} fontSize="xs" color="fg.muted" fontWeight="normal">
                      Reliance
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {node.scopes.map(scope => (
                    <Table.Row key={scope.scope}>
                      <Table.Cell px={2} py={1.5} fontSize="sm" fontWeight="semibold" color="gray.800">
                        {scope.scope}
                      </Table.Cell>
                      <Table.Cell px={2} py={1.5} fontSize="xs">
                        <ConclusionValue scope={scope} />
                      </Table.Cell>
                      <Table.Cell px={2} py={1.5}>
                        {scope.supportsReliance && <RelianceBadge />}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Popover.Body>
        </Popover.Content>
      </Popover.Positioner>
    </Portal>
  </Popover.Root>
));

export default ScopedControlPopover;
